import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { NumberTypeEnum, TwilioPhoneNumber, useavailablePhoneNumbersLazyQuery } from '@/types/generated/graphql';
import { useEncryptedParams } from '@shared/hooks/use-encrypted-params';
import { PurchaseNumberKey } from '@modules/app-redirection-center/hooks/website-purchase-number.types';
import { WebsiteNumberPurchaseType } from '@modules/app-redirection-center/hooks/use-website-number-purchase';
import { LocalStorageKeys } from '@shared/constants';
import { useChannelReward } from './hooks/use-channel-reward';
import { claimNumbersModalStore } from './store/claim-numbers-modal.store';

export function useClaimNumbers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useEncryptedParams();
  const websiteNumberPurchaseType = params.get('websiteNumberPurchaseType') as WebsiteNumberPurchaseType;

  const { availableRewardCount } = useChannelReward('network-only');
  const claimModal = claimNumbersModalStore();

  const [prefill, setPrefill] = useState<TwilioPhoneNumber | null>(null);

  const claimNumberSkipped = localStorage.getItem(LocalStorageKeys.claimNumberSkipped) === 'true';
  const openClaimNumberModal = availableRewardCount > 0 && !claimNumberSkipped;

  const [checkNumberExists] = useavailablePhoneNumbersLazyQuery({ fetchPolicy: 'network-only' });

  useEffect(
    function openFreeClaimModalOnPageLoad() {
      // stop free modal claim modal from opening if user chooses unclaimable numbers from website
      if (websiteNumberPurchaseType && websiteNumberPurchaseType !== 'free') {
        // handled in src/modules/settings/my-numbers/purchase-number/components/add-to-cart-button.tsx
        return;
      }

      const numberDetail = localStorage.getItem(PurchaseNumberKey);
      const numberObj = numberDetail ? (JSON.parse(numberDetail) as TwilioPhoneNumber) : null;

      if (websiteNumberPurchaseType === 'free' && numberObj?.phoneNumber) {
        localStorage.removeItem(PurchaseNumberKey);

        // the number may have been claimed by someone else since it was picked on the website,
        // so re-verify it's still available in Twilio before pre-selecting it in the modal

        const variables = {
          countryCode: numberObj?.isoCountry ?? '',
          numberType: numberObj?.numberType as NumberTypeEnum,
          contains: numberObj?.phoneNumber,
        };

        checkNumberExists({ variables }).then(({ data }) => {
          const twilioPhNumber = data?.availablePhoneNumbers?.edges?.find(
            (edge) => edge?.phoneNumber === numberObj?.phoneNumber
          );

          if (!twilioPhNumber) {
            notification.error({ message: t('selected_number_not_available_try_again') });
            navigate('/settings/my-numbers/purchase-number');
            return;
          }

          setPrefill(twilioPhNumber);

          if (openClaimNumberModal) {
            claimModal?.handleOpen();
          }
        });

        return;
      }

      if (openClaimNumberModal) {
        claimModal?.handleOpen();
      }
    },
    [openClaimNumberModal]
  );

  // Called once a claim succeeds so a later reopen (e.g. more free numbers became available)
  // doesn't resurface this already-claimed number as the prefill.
  const resetPrefill = () => {
    localStorage.removeItem(PurchaseNumberKey);
    setPrefill(null);
  };

  return { claimModal, availableRewardCount, prefill, resetPrefill };
}
